<script setup lang="ts">
  defineOptions({
    name: 'AppSidebarNavItem',
    inheritAttrs: false,
  })

  import type { PropType } from 'vue'
  import { computed } from 'vue'
  import type { RouteLocationRaw } from 'vue-router'

  const props = defineProps({
    to: {
      type: [String, Object] as PropType<string | RouteLocationRaw>,
      required: true,
    },
    isActive: {
      type: Boolean,
      default: false,
    },
    linkId: {
      type: String,
      default: undefined,
    },
    ariaLabel: {
      type: String,
      default: undefined,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    activeClass: {
      type: String,
      default: 'bg-primary text-on-primary focus-visible:ring-[var(--cp-text-on-primary)]',
    },
    inactiveClass: {
      type: String,
      default: 'text-on-sidebar hover:bg-sidebar-hover focus-visible:ring-primary',
    },
  })

  const emit = defineEmits<{
    click: [event: MouseEvent]
  }>()

  const linkClass = computed(() =>
    [
      'app-sidebar-nav-item__link',
      'flex h-8 w-full min-w-0 cursor-pointer items-center justify-start gap-2 rounded-md p-2 text-nowrap',
      'text-inherit no-underline visited:no-underline hover:no-underline outline-none transition-colors',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset',
      'group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:gap-0',
      props.isActive ? props.activeClass : props.inactiveClass,
      props.disabled ? 'pointer-events-none opacity-50' : '',
    ]
      .filter(Boolean)
      .join(' ')
  )

  const handleClick = (event: MouseEvent) => {
    if (props.disabled) {
      event.preventDefault()
      return
    }

    emit('click', event)
  }
</script>

<template>
  <li class="app-sidebar-nav-item relative m-0 w-full list-none p-0">
    <NuxtLink
      v-bind="$attrs"
      :id="linkId"
      :to="to"
      :class="linkClass"
      :aria-current="isActive ? 'page' : undefined"
      :aria-label="ariaLabel"
      :aria-disabled="disabled ? 'true' : undefined"
      :tabindex="disabled ? -1 : undefined"
      @click="handleClick"
    >
      <slot name="leading" />
      <span
        class="app-sidebar-nav-item__label min-w-0 truncate group-data-[collapsible=icon]:w-0 group-data-[collapsible=icon]:min-w-0 group-data-[collapsible=icon]:overflow-hidden group-data-[collapsible=icon]:opacity-0"
      >
        <slot />
      </span>
    </NuxtLink>
    <div
      v-if="$slots.trailing"
      class="app-sidebar-nav-item__trailing pointer-events-auto absolute inset-y-0 right-0 z-20 flex items-center pr-2"
    >
      <slot name="trailing" />
    </div>
  </li>
</template>
